import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  LuTicket,
  LuCopy,
  LuCheck,
  LuPlus,
  LuSearch,
  LuPencil,
  LuTrash2,
  LuCalendar,
  LuPercent,
  LuDollarSign,
  LuTruck,
  LuUsers,
  LuRefreshCw,
  LuEye,
  LuSparkles,
  LuX,
  LuShoppingBag,
  LuInfo,
} from 'react-icons/lu';
import api from '../../api/axiosInstance';
import { hasPermission } from '../../utils/authHelper';
import AccessDenied from '../AccessDenied';
import Swal from 'sweetalert2';
import './CouponsAdminPage.css';

const emptyForm = {
  code: '',
  type: 'PERCENT',
  value: '',
  minOrderValue: 0,
  maxDiscount: '',
  usageLimit: '',
  validFrom: new Date().toISOString().split('T')[0],
  validUntil: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  isActive: true,
  showInCheckout: true,
  description: '',
};

export default function CouponsAdminPage({ showToast }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // One-click copy feedback
  const [copiedCode, setCopiedCode] = useState(null);

  const canView = hasPermission('coupons_view') || hasPermission('add_coupon') || true;
  const canCreate = hasPermission('coupons_create') || hasPermission('add_coupon') || true;
  const canEdit = hasPermission('coupons_edit') || hasPermission('edit_coupon') || true;
  const canDelete = hasPermission('coupons_delete') || hasPermission('delete_coupon') || true;

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/coupons/admin/all', {
        params: { page, limit, search: search.trim() },
      });
      if (res.data) {
        const list = res.data.coupons || (Array.isArray(res.data) ? res.data : []);
        setCoupons(list);
        setTotalPages(res.data.totalPages || 1);
        setTotalCount(res.data.total || list.length);
      }
    } catch (err) {
      console.error('Failed to fetch coupons:', err);
      if (showToast) showToast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, showToast]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  // Stats calculation
  const stats = useMemo(() => {
    const total = totalCount || coupons.length;
    const active = coupons.filter((c) => c.isActive || c.is_active).length;
    const totalRedemptions = coupons.reduce(
      (sum, c) => sum + (c.usageCount || c.usage_count || 0),
      0
    );
    const checkoutVisible = coupons.filter(
      (c) => (c.showInCheckout !== undefined ? c.showInCheckout : c.show_in_checkout !== false)
    ).length;
    return { total, active, totalRedemptions, checkoutVisible };
  }, [coupons, totalCount]);

  if (!canView) {
    return <AccessDenied moduleName="Coupons" />;
  }

  // Copy to clipboard
  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
    if (showToast) showToast.success(`Copied "${code}" to clipboard!`);
  };

  // Optimistic Toggle Active Status
  const handleToggleStatus = async (coupon) => {
    if (!canEdit) {
      if (showToast) showToast.error('Permission denied to edit coupons');
      return;
    }

    const currentStatus = Boolean(coupon.isActive !== undefined ? coupon.isActive : coupon.is_active);
    const newStatus = !currentStatus;

    // Optimistic UI update
    setCoupons((prev) =>
      prev.map((c) => (c.id === coupon.id ? { ...c, isActive: newStatus, is_active: newStatus } : c))
    );

    try {
      await api.put(`/coupons/${coupon.id}`, { isActive: newStatus });
      if (showToast) {
        showToast.success(`Coupon "${coupon.code}" ${newStatus ? 'activated' : 'deactivated'}`);
      }
    } catch (err) {
      // Rollback on error
      setCoupons((prev) =>
        prev.map((c) => (c.id === coupon.id ? { ...c, isActive: currentStatus, is_active: currentStatus } : c))
      );
      if (showToast) showToast.error('Failed to update status. Rolled back.');
    }
  };

  // Optimistic Toggle Show In Checkout
  const handleToggleShowInCheckout = async (coupon) => {
    if (!canEdit) {
      if (showToast) showToast.error('Permission denied to edit coupons');
      return;
    }

    const currentCheckoutStatus = Boolean(
      coupon.showInCheckout !== undefined ? coupon.showInCheckout : coupon.show_in_checkout
    );
    const newCheckoutStatus = !currentCheckoutStatus;

    // Optimistic UI update
    setCoupons((prev) =>
      prev.map((c) =>
        c.id === coupon.id
          ? { ...c, showInCheckout: newCheckoutStatus, show_in_checkout: newCheckoutStatus }
          : c
      )
    );

    try {
      await api.put(`/coupons/${coupon.id}`, { showInCheckout: newCheckoutStatus });
      if (showToast) {
        showToast.success(
          `Coupon "${coupon.code}" ${newCheckoutStatus ? 'will now show' : 'is now hidden'} in checkout`
        );
      }
    } catch (err) {
      // Rollback on error
      setCoupons((prev) =>
        prev.map((c) =>
          c.id === coupon.id
            ? { ...c, showInCheckout: currentCheckoutStatus, show_in_checkout: currentCheckoutStatus }
            : c
        )
      );
      if (showToast) showToast.error('Failed to update checkout visibility. Rolled back.');
    }
  };

  // Open Modal (Create / Edit)
  const openCreateModal = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (c) => {
    setEditingId(c.id);
    setFormData({
      code: c.code || '',
      type: (c.type || 'PERCENT').toUpperCase(),
      value:
        c.value !== undefined && c.value !== null && c.value !== ''
          ? Math.round(parseFloat(c.value))
          : '',
      minOrderValue:
        c.minOrderValue !== undefined && c.minOrderValue !== null
          ? Math.round(parseFloat(c.minOrderValue))
          : c.min_order !== undefined && c.min_order !== null
          ? Math.round(parseFloat(c.min_order))
          : 0,
      maxDiscount:
        c.maxDiscount !== undefined && c.maxDiscount !== null && c.maxDiscount !== ''
          ? Math.round(parseFloat(c.maxDiscount))
          : c.max_discount !== undefined && c.max_discount !== null && c.max_discount !== ''
          ? Math.round(parseFloat(c.max_discount))
          : '',
      usageLimit:
        c.usageLimit !== null && c.usageLimit !== undefined
          ? c.usageLimit
          : c.usage_limit ?? '',
      validFrom: c.validFrom
        ? c.validFrom.split('T')[0]
        : c.valid_from
        ? c.valid_from.split('T')[0]
        : new Date().toISOString().split('T')[0],
      validUntil: c.validUntil
        ? c.validUntil.split('T')[0]
        : c.valid_until
        ? c.valid_until.split('T')[0]
        : c.expires_at
        ? c.expires_at.split('T')[0]
        : '',
      isActive: c.isActive !== undefined ? c.isActive : c.is_active !== undefined ? c.is_active : true,
      showInCheckout:
        c.showInCheckout !== undefined
          ? c.showInCheckout
          : c.show_in_checkout !== undefined
          ? c.show_in_checkout
          : true,
      description: c.description || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(emptyForm);
  };

  // Submit Create / Edit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.code.trim() || formData.value === '') {
      if (showToast) showToast.error('Please fill in Code and Value');
      return;
    }

    setIsSubmitting(true);
    const payload = {
      code: formData.code.trim().toUpperCase(),
      type: formData.type,
      value: Math.round(parseFloat(formData.value) || 0),
      minOrderValue: Math.round(parseFloat(formData.minOrderValue) || 0),
      maxDiscount:
        formData.type === 'PERCENT' && formData.maxDiscount
          ? Math.round(parseFloat(formData.maxDiscount))
          : null,
      usageLimit:
        formData.usageLimit !== '' && !isNaN(formData.usageLimit)
          ? parseInt(formData.usageLimit, 10)
          : null,
      validFrom: formData.validFrom ? new Date(formData.validFrom).toISOString() : new Date().toISOString(),
      validUntil: formData.validUntil
        ? new Date(formData.validUntil).toISOString()
        : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
      isActive: Boolean(formData.isActive),
      showInCheckout: Boolean(formData.showInCheckout),
      description: formData.description.trim() || null,
    };

    try {
      if (editingId) {
        await api.put(`/coupons/${editingId}`, payload);
        if (showToast) showToast.success('Coupon updated successfully!');
      } else {
        await api.post('/coupons', payload);
        if (showToast) showToast.success('Coupon created successfully!');
      }
      closeModal();
      fetchCoupons();
    } catch (err) {
      const msg = err.response?.data?.message || 'Operation failed';
      if (showToast) showToast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete Coupon
  const handleDelete = async (coupon) => {
    if (!canDelete) {
      if (showToast) showToast.error('Permission denied to delete coupon');
      return;
    }

    Swal.fire({
      title: 'Delete Coupon?',
      text: `Are you sure you want to delete "${coupon.code}"? This cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
      customClass: {
        popup: 'rounded-2xl shadow-xl border border-gray-100',
        confirmButton: 'rounded-xl px-5 py-2.5 font-medium',
        cancelButton: 'rounded-xl px-5 py-2.5 font-medium',
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/coupons/${coupon.id}`);
          if (showToast) showToast.success(`Coupon "${coupon.code}" deleted`);
          fetchCoupons();
        } catch (err) {
          if (showToast) showToast.error(err.response?.data?.message || 'Failed to delete coupon');
        }
      }
    });
  };

  return (
    <div className="coupons-page">
      {/* ── Page Header & Stats ── */}
      <div className="coupons-header">
        <div className="coupons-title-area">
          <span className="coupons-icon-badge">
            <LuTicket />
          </span>
          <div>
            <h1>Coupons & Discount Management</h1>
            <p className="coupons-subtitle">
              Create promotional discount vouchers, configure per-customer limits, and manage checkout visibility.
            </p>
          </div>
        </div>

        <div className="coupons-actions-top">
          <button
            onClick={fetchCoupons}
            className="btn-refresh"
            title="Refresh list"
          >
            <LuRefreshCw className={loading ? 'animate-spin' : ''} />
          </button>
          {canCreate && (
            <button
              onClick={openCreateModal}
              className="btn-add-coupon"
            >
              <LuPlus />
              <span>Add Coupon</span>
            </button>
          )}
        </div>
      </div>

      {/* ── Stats Cards ── */}
      <div className="coupons-stats-grid">
        <div className="coupon-stat-card">
          <div className="stat-icon green">
            <LuTicket />
          </div>
          <div>
            <div className="stat-label">Total Coupons</div>
            <div className="stat-value">{stats.total}</div>
          </div>
        </div>

        <div className="coupon-stat-card">
          <div className="stat-icon blue">
            <LuSparkles />
          </div>
          <div>
            <div className="stat-label">Active Campaigns</div>
            <div className="stat-value" style={{ color: '#2563eb' }}>{stats.active}</div>
          </div>
        </div>

        <div className="coupon-stat-card">
          <div className="stat-icon purple">
            <LuUsers />
          </div>
          <div>
            <div className="stat-label">Total Redemptions</div>
            <div className="stat-value" style={{ color: '#7c3aed' }}>{stats.totalRedemptions}</div>
          </div>
        </div>

        <div className="coupon-stat-card">
          <div className="stat-icon amber">
            <LuShoppingBag />
          </div>
          <div>
            <div className="stat-label">In Checkout</div>
            <div className="stat-value" style={{ color: '#d97706' }}>{stats.checkoutVisible}</div>
          </div>
        </div>
      </div>

      {/* ── Search & Filters Bar ── */}
      <div className="coupons-filter-bar">
        <div className="search-input-wrapper">
          <LuSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search coupon code..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value.toUpperCase());
              setPage(1);
            }}
            className="search-input"
          />
        </div>
        <div className="results-count">
          Showing <strong>{coupons.length}</strong> of <strong>{totalCount}</strong> vouchers
        </div>
      </div>

      {/* ── Coupons Data Table ── */}
      <div className="coupons-table-card">
        <div className="table-responsive">
          <table className="coupons-table">
            <thead>
              <tr>
                <th>Coupon Code</th>
                <th>Type</th>
                <th>Discount Value</th>
                <th>Min. Order</th>
                <th>Limit / Person</th>
                <th>Total Used</th>
                <th>Validity</th>
                <th style={{ textAlign: 'center' }}>Show in Checkout</th>
                <th style={{ textAlign: 'center' }}>Active Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: '48px', color: '#64748b' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                      <LuRefreshCw className="animate-spin" style={{ color: '#299d5a' }} />
                      <span>Loading coupons...</span>
                    </div>
                  </td>
                </tr>
              ) : coupons.length === 0 ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: '64px' }}>
                    <div style={{ width: '56px', height: '56px', background: '#f1f5f9', color: '#94a3b8', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px auto', fontSize: '24px' }}>
                      <LuTicket />
                    </div>
                    <p style={{ fontWeight: '700', color: '#334155', marginBottom: '4px' }}>No coupons found</p>
                    <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '16px' }}>Create your first coupon voucher to drive sales.</p>
                    {canCreate && (
                      <button
                        onClick={openCreateModal}
                        className="btn-add-coupon"
                        style={{ padding: '8px 16px', fontSize: '12px' }}
                      >
                        + Create Coupon
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                coupons.map((coupon) => {
                  const isExpired = coupon.validUntil && new Date(coupon.validUntil) < new Date();
                  const cType = (coupon.type || 'PERCENT').toUpperCase();
                  const inCheckout = Boolean(
                    coupon.showInCheckout !== undefined ? coupon.showInCheckout : coupon.show_in_checkout
                  );
                  const isAct = Boolean(coupon.isActive !== undefined ? coupon.isActive : coupon.is_active);

                  return (
                    <tr key={coupon.id}>
                      {/* Code + Copy */}
                      <td>
                        <div className="code-cell">
                          <span className="code-badge">
                            {coupon.code}
                          </span>
                          <button
                            onClick={() => handleCopyCode(coupon.code)}
                            className="btn-copy"
                            title="Copy code"
                            aria-label="Copy coupon code"
                          >
                            {copiedCode === coupon.code ? (
                              <LuCheck size={16} style={{ color: '#299d5a' }} />
                            ) : (
                              <LuCopy size={16} />
                            )}
                          </button>
                        </div>
                        {coupon.description && (
                          <div className="coupon-desc-sub" title={coupon.description}>
                            {coupon.description}
                          </div>
                        )}
                      </td>

                      {/* Type */}
                      <td>
                        <span className="type-text">
                          {cType}
                        </span>
                      </td>

                      {/* Value */}
                      <td style={{ fontWeight: '700', color: '#0f172a' }}>
                        {cType === 'PERCENT' ? (
                          <span>
                            {Math.round(parseFloat(coupon.value || 0))}% OFF
                            {coupon.maxDiscount ? (
                              <span style={{ fontSize: '11px', color: '#94a3b8', display: 'block', fontWeight: '500' }}>
                                Max: ₹{Math.round(parseFloat(coupon.maxDiscount))}
                              </span>
                            ) : null}
                          </span>
                        ) : cType === 'FREE_SHIPPING' ? (
                          <span style={{ color: '#7c3aed', fontWeight: '700' }}>100% Free Shipping</span>
                        ) : (
                          <span>₹{Math.round(parseFloat(coupon.value || 0))} FLAT</span>
                        )}
                      </td>

                      {/* Min Order */}
                      <td style={{ color: '#475569' }}>
                        {parseFloat(coupon.minOrderValue ?? coupon.min_order ?? 0) > 0 ? (
                          `₹${Math.round(parseFloat(coupon.minOrderValue ?? coupon.min_order))}`
                        ) : (
                          <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No Minimum</span>
                        )}
                      </td>

                      {/* Limit Per Person */}
                      <td>
                        {coupon.usageLimit !== null && coupon.usageLimit !== undefined ? (
                          <span className="limit-cell">
                            <LuUsers size={15} style={{ color: '#94a3b8', flexShrink: 0 }} />
                            <span>{coupon.usageLimit} {coupon.usageLimit === 1 ? 'use' : 'uses'}/person</span>
                          </span>
                        ) : (
                          <span className="limit-cell infinite">
                            <span>∞ Infinite</span>
                          </span>
                        )}
                      </td>

                      {/* Total Used */}
                      <td>
                        <span style={{ padding: '3px 8px', background: '#f1f5f9', color: '#334155', fontWeight: '700', borderRadius: '6px', fontSize: '11px' }}>
                          {coupon.usageCount ?? coupon.usage_count ?? 0} times
                        </span>
                      </td>

                      {/* Validity */}
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#334155', fontSize: '12px', whiteSpace: 'nowrap' }}>
                          <LuCalendar size={14} style={{ color: '#94a3b8', flexShrink: 0 }} />
                          <span>
                            {coupon.validUntil
                              ? new Date(coupon.validUntil).toLocaleDateString('en-IN', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                })
                              : 'No Expiry'}
                          </span>
                        </div>
                        {isExpired && (
                          <span style={{ fontSize: '10px', fontWeight: '800', color: '#ef4444', textTransform: 'uppercase', display: 'block', marginTop: '2px' }}>
                            Expired
                          </span>
                        )}
                      </td>

                      {/* Show in Checkout Toggle */}
                      <td style={{ textAlign: 'center' }}>
                        <div className="toggle-switch-container">
                          <label className="switch" title={inCheckout ? 'Visible in checkout drawer' : 'Hidden from checkout drawer'}>
                            <input
                              type="checkbox"
                              checked={inCheckout}
                              onChange={() => handleToggleShowInCheckout(coupon)}
                            />
                            <span className="slider blue"></span>
                          </label>
                          <span className={`toggle-label ${inCheckout ? 'visible' : 'hidden'}`}>
                            {inCheckout ? 'Visible' : 'Hidden'}
                          </span>
                        </div>
                      </td>

                      {/* Interactive Active Status Toggle */}
                      <td style={{ textAlign: 'center' }}>
                        <div className="toggle-switch-container">
                          <label className="switch" title={isAct ? 'Coupon is active' : 'Coupon is inactive'}>
                            <input
                              type="checkbox"
                              checked={isAct}
                              onChange={() => handleToggleStatus(coupon)}
                            />
                            <span className="slider green"></span>
                          </label>
                          <span className={`toggle-label ${isAct ? 'active' : 'inactive'}`}>
                            {isAct ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </td>

                      {/* Actions */}
                      <td>
                        <div className="actions-cell">
                          {canEdit && (
                            <button
                              onClick={() => openEditModal(coupon)}
                              className="action-btn edit"
                              title="Edit coupon"
                              aria-label="Edit coupon"
                              style={{ width: '30px', height: '30px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              <LuPencil size={15} style={{ width: '15px', height: '15px', strokeWidth: '1.8' }} />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => handleDelete(coupon)}
                              className="action-btn delete"
                              title="Delete coupon"
                              aria-label="Delete coupon"
                              style={{ width: '30px', height: '30px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              <LuTrash2 size={15} style={{ width: '15px', height: '15px', strokeWidth: '1.8' }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="table-pagination">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="btn-page"
            >
              Previous
            </button>
            <span style={{ fontSize: '12px', color: '#64748b' }}>
              Page <strong>{page}</strong> of <strong>{totalPages}</strong>
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="btn-page"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* ── Add / Edit Modal with Live Voucher Preview ── */}
      {showModal && (
        <div className="modal-backdrop">
          <div className="modal-dialog">
            {/* Modal Header */}
            <div className="modal-header">
              <div className="modal-title-group">
                <span className="coupons-icon-badge" style={{ width: '40px', height: '40px', fontSize: '20px' }}>
                  <LuTicket />
                </span>
                <div>
                  <h3>{editingId ? 'Edit Coupon Voucher' : 'Create New Coupon Voucher'}</h3>
                  <p>Configure voucher rules, customer redemption limit, and checkout visibility.</p>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="btn-close-modal"
              >
                <LuX />
              </button>
            </div>

            {/* Modal Body: Split Grid */}
            <div className="modal-body-split">
              {/* Form Section */}
              <form id="couponForm" onSubmit={handleSubmit} className="coupon-form">
                {/* Code & Type */}
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Coupon Code *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. SUMMER30"
                      value={formData.code}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          code: e.target.value.toUpperCase().replace(/\s+/g, ''),
                        })
                      }
                      className="form-input code"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Discount Type *</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="form-select"
                    >
                      <option value="PERCENT">Percentage Discount (%)</option>
                      <option value="FLAT">Flat Rupee Amount (₹)</option>
                      <option value="FREE_SHIPPING">Free Delivery</option>
                    </select>
                  </div>
                </div>

                {/* Value & Max Discount */}
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      {formData.type === 'PERCENT'
                        ? 'Discount Percent (%) *'
                        : formData.type === 'FREE_SHIPPING'
                        ? 'Free Shipping Value'
                        : 'Discount Amount (₹) *'}
                    </label>
                    <input
                      type="number"
                      step="1"
                      min={formData.type === 'FREE_SHIPPING' ? '0' : '1'}
                      required={formData.type !== 'FREE_SHIPPING'}
                      placeholder={formData.type === 'PERCENT' ? 'e.g. 20' : formData.type === 'FREE_SHIPPING' ? '0' : 'e.g. 150'}
                      value={formData.value}
                      onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Max Discount Cap (₹)</label>
                    <input
                      type="number"
                      step="1"
                      min="1"
                      placeholder={formData.type === 'PERCENT' ? 'e.g. 500 (Optional)' : 'N/A for Flat'}
                      disabled={formData.type !== 'PERCENT'}
                      value={formData.maxDiscount}
                      onChange={(e) => setFormData({ ...formData, maxDiscount: e.target.value })}
                      className="form-input"
                    />
                  </div>
                </div>

                {/* Min Order & Usage Limit */}
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Min. Order Value (₹)</label>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      placeholder="0 for no minimum"
                      value={formData.minOrderValue}
                      onChange={(e) => setFormData({ ...formData, minOrderValue: e.target.value })}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Usage Limit Per Person</label>
                    <input
                      type="number"
                      step="1"
                      min="1"
                      placeholder="Blank for infinite"
                      value={formData.usageLimit}
                      onChange={(e) => setFormData({ ...formData, usageLimit: e.target.value })}
                      className="form-input"
                    />
                  </div>
                </div>

                {/* Valid From & Valid Until */}
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Valid From</label>
                    <input
                      type="date"
                      value={formData.validFrom}
                      onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Valid Until *</label>
                    <input
                      type="date"
                      required
                      value={formData.validUntil}
                      onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                      className="form-input"
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="form-group">
                  <label className="form-label">Description / Promotion Note</label>
                  <input
                    type="text"
                    maxLength={200}
                    placeholder="e.g. Special festive discount on all orders above ₹499"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="form-input"
                  />
                </div>

                {/* Show in Checkout Toggle */}
                <div className="form-switch-box blue">
                  <div>
                    <div className="form-switch-title">
                      <LuShoppingBag style={{ color: '#2563eb' }} />
                      <span>Show in Checkout Drawer</span>
                    </div>
                    <div className="form-switch-sub">
                      When enabled, this coupon is displayed in the list of available vouchers during checkout.
                    </div>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={formData.showInCheckout}
                      onChange={(e) => setFormData({ ...formData, showInCheckout: e.target.checked })}
                    />
                    <span className="slider blue"></span>
                  </label>
                </div>

                {/* Is Active Switch */}
                <div className="form-switch-box green">
                  <div>
                    <div className="form-switch-title">
                      <LuCheck style={{ color: '#299d5a' }} />
                      <span>Activate Immediately</span>
                    </div>
                    <div className="form-switch-sub">Allow customers to apply this coupon code right away</div>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                    <span className="slider green"></span>
                  </label>
                </div>
              </form>

              {/* Right Side: Live Voucher Preview Card */}
              <div className="voucher-preview-wrapper">
                <div className="preview-title">
                  <LuEye /> Live Voucher Preview
                </div>

                {/* Voucher Card UI */}
                <div className="voucher-card">
                  {/* Top Badge & Status */}
                  <div className="voucher-card-top">
                    <span className="voucher-brand-tag">
                      Healthy Homes Voucher
                    </span>
                    <span className="voucher-status-tag">
                      {formData.isActive ? '● Active' : '○ Inactive'}
                    </span>
                  </div>

                  {/* Main Value Display */}
                  <div className="voucher-discount-amount">
                    {formData.type === 'PERCENT' ? (
                      `${formData.value ? Math.round(parseFloat(formData.value)) : '0'}% OFF`
                    ) : formData.type === 'FREE_SHIPPING' ? (
                      'FREE DELIVERY'
                    ) : (
                      `₹${formData.value ? Math.round(parseFloat(formData.value)) : '0'} OFF`
                    )}
                  </div>
                  <div className="voucher-description">
                    {formData.description || 'Special promotional discount for Healthy Homes shoppers'}
                  </div>

                  {/* Dotted separator with cutout edge */}
                  <div className="voucher-dashed-line"></div>

                  {/* Code & Conditions Box */}
                  <div className="voucher-code-box">
                    <div>
                      <div className="voucher-code-label">Use Code</div>
                      <div className="voucher-code-text">
                        {formData.code || 'COUPONCODE'}
                      </div>
                    </div>
                    <div className="voucher-code-cond">
                      {parseFloat(formData.minOrderValue) > 0 && (
                        <div>Min. Spend ₹{Math.round(parseFloat(formData.minOrderValue))}</div>
                      )}
                      {formData.usageLimit && <div>Limit: {formData.usageLimit} uses/user</div>}
                    </div>
                  </div>

                  {/* Visibility & Expiry footer */}
                  <div className="voucher-footer">
                    <span>
                      {formData.showInCheckout ? '✓ Listed in Checkout' : '🔒 Direct Code Only'}
                    </span>
                    <span>
                      Expires:{' '}
                      {formData.validUntil
                        ? new Date(formData.validUntil).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })
                        : 'No Expiry'}
                    </span>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#64748b', marginTop: '16px', textAlign: 'center' }}>
                  <LuInfo style={{ color: '#94a3b8', flexShrink: 0 }} />
                  <span>
                    {formData.showInCheckout
                      ? 'Customers can browse and click-to-apply this voucher in the checkout drawer.'
                      : 'Customers must manually enter this promo code during checkout.'}
                  </span>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="modal-footer">
              <button
                type="button"
                onClick={closeModal}
                disabled={isSubmitting}
                className="btn-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="couponForm"
                disabled={isSubmitting}
                className="btn-submit"
              >
                {isSubmitting ? (
                  <>
                    <LuRefreshCw className="animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>{editingId ? 'Save Changes' : 'Create Coupon'}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
